import { toast } from 'react-toastify';
import { User } from '../../types';
import { checkResponse, fetchWithAuth } from '../../util';
import { selectUserAuth, setUser } from '../auth/authSlice';
import { AppThunk } from '../store';
import { UserDto, UserResetDto, UserSetDto } from './userDto';
import { selectUsersList, setUsers } from './userSlice';
import { GENERIC_APP } from '../../config/generic';
import { SOS_APP } from '../../config/sos';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const USER_LIST_URL = new URL('user', BASE_URL);
const USER_SET_URL = new URL('user/set', BASE_URL);
const USER_DELETE_URL = new URL('user/', BASE_URL);
const USER_FORGOT_PASSWORD_URL = new URL('user/forgot-password', BASE_URL);
const USER_RESET_PWD_URL = new URL('user/reset-pwd', BASE_URL);

function mapDtoToUser({
  created_at,
  updated_at,
  actor,
  ...others
}: UserDto): User {
  return {
    ...others,
    actor: actor ?? [],
    createdAt: created_at,
    updatedAt: updated_at,
  };
}

export const getUserList = (): AppThunk => async (dispatch) => {
  const res = await checkResponse(fetchWithAuth(USER_LIST_URL), {
    toastOnApiError: true,
    toastOnFetchError: true,
  });
  if (res) {
    const data: UserDto[] = await res.json();
    dispatch(setUsers(data.map(mapDtoToUser)));
  }
};

export const setUserThunk =
  (user: UserSetDto): AppThunk =>
  async (dispatch, getState) => {
    const res = await checkResponse(
      fetchWithAuth(USER_SET_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user),
      }),
      {
        toastOnApiError: true,
        toastOnFetchError: true,
      }
    );
    if (res) {
      const data: UserDto = await res.json();
      const updatedUser = mapDtoToUser(data);

      const state = getState();
      const users = selectUsersList(state);
      const userSelf = selectUserAuth(state);
      dispatch(
        setUsers(users.map((u) => (u.id === updatedUser.id ? updatedUser : u)))
      );
      if (updatedUser.id === userSelf?.id) {
        dispatch(setUser(updatedUser));
      }
      toast.success('User updated!');
    }
  };

export const deleteUserThunk =
  (userId: string): AppThunk =>
  async (dispatch, getState) => {
    const res = await checkResponse(
      fetchWithAuth(USER_DELETE_URL + userId, {
        method: 'DELETE',
      }),
      { toastOnApiError: true, toastOnFetchError: true }
    );
    if (res) {
      const users = selectUsersList(getState());
      dispatch(setUsers(users.filter((u) => u.id !== userId)));
      toast.success('User deleted!');
    }
  };

export const requestPasswordResetThunk =
  (user: UserResetDto): AppThunk<Promise<boolean>> =>
  async () => {
    const res = await checkResponse(
      fetch(USER_FORGOT_PASSWORD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user.username }),
      }),
      {
        toastOnApiError: true,
        toastOnFetchError: true,
      }
    );
    if (res) {
      const appName =
        process.env.REACT_APP_DEPLOYMENT_TYPE === 'GENERIC'
          ? GENERIC_APP.name
          : SOS_APP.name;
      toast.success(
        `Reset instructions sent. Check your email for ${appName}.`
      );
      return true;
    }

    return false;
  };

export const confirmPasswordResetThunk =
  (user: UserResetDto): AppThunk<Promise<boolean>> =>
  async () => {
    const res = await checkResponse(
      fetch(USER_RESET_PWD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: user.username,
          code: user.code,
          password: user.password,
        }),
      }),
      {
        toastOnApiError: true,
        toastOnFetchError: true,
      }
    );
    if (res) {
      toast.success('Password updated. You can now log in.');
      return true;
    }

    return false;
  };
